<div
  #trigger
  cdkOverlayOrigin
  #overlayOrigin="cdkOverlayOrigin"
  class="sg-dropdown__container"
  [class.sg-dropdown--focused]="focused || isOpen"
  [class.sg-dropdown--floating]="shouldFloat"
  [class.sg-dropdown--has-prefix]="prefixIcon"
  [class.sg-dropdown--clearable]="clearable"
  [class.sg-dropdown--disabled]="disabled"
  [class.sg-dropdown--invalid]="isInvalid"
  [class.sg-dropdown--small]="size === 'small'"
  [class.sg-dropdown--secondary]="variant === 'secondary'"
  [class.sg-dropdown--underline]="variant === 'underline'"
  [class.sg-dropdown--open]="isOpen"
  tabindex="0"
  role="combobox"
  [attr.aria-expanded]="isOpen"
  [attr.aria-haspopup]="'listbox'"
  [attr.aria-disabled]="disabled"
  [attr.aria-invalid]="isInvalid"
  (click)="toggle()"
  (focus)="onFocus()"
  (blur)="onBlur()">
  <!-- The outline structure -->
  <div class="sg-dropdown__outline">
    <div class="sg-dropdown__outline-start"></div>
    <div class="sg-dropdown__outline-notch"></div>
    <div class="sg-dropdown__outline-end"></div>
  </div>
  <!-- Floating label -->
  @if (label) {
    <label class="sg-dropdown__label" [class.sg-label-medium-regular]="!shouldFloat" [class.sg-label-small-regular]="shouldFloat">
      {{ label }}
      @if (isRequired && !disabled) {
        <span class="sg-dropdown__required">*</span>
      }
      @if (labelTooltip && shouldFloat) {
        <sg-info-tooltip
          class="sg-dropdown__info sg-dropdown__info--label"
          variant="sg-label-small-regular"
          [heading]="labelTooltipHeading"
          [text]="labelTooltip"
          (click)="$event.stopPropagation()"></sg-info-tooltip>
      }
    </label>
  }
  <!-- Trigger content wrapper -->
  <div class="sg-dropdown__wrapper">
    @if (prefixIcon) {
      <mat-icon class="sg-dropdown__prefix">{{ prefixIcon }}</mat-icon>
    }
    @if (!empty) {
      <span class="sg-dropdown__value" [class.sg-body-medium-regular]="size !== 'small'" [class.sg-body-small-regular]="size === 'small'">
        <!-- Single select display -->
        @if (!multiple) {
          {{ displayValue }}
        }
        <!-- Multi-select: Count mode -->
        @if (multiple && selectedCount > 0 && multiSelectDisplay === "count") {
          <span class="sg-dropdown__count-display" [class.sg-body-medium-regular]="size !== 'small'" [class.sg-body-small-regular]="size === 'small'">
            {{ selectedCount }} selected
          </span>
        }
        <!-- Multi-select: Chips mode -->
        @if (multiple && selectedCount > 0 && multiSelectDisplay === "chips") {
          <div class="sg-dropdown__chips">
            <!-- Stacked indicator (shows count of additional items) -->
            @if (additionalSelectedCount > 0) {
              <div class="sg-dropdown__chip sg-dropdown__chip--stacked" [sgTooltip]="selectedLabelsTooltip">
                <span class="sg-dropdown__chip-count sg-label-small-regular">+{{ additionalSelectedCount }}</span>
                <button
                  type="button"
                  class="sg-dropdown__chip-remove"
                  tabindex="-1"
                  (mousedown)="$event.preventDefault()"
                  (click)="clearAllButLast($event)"
                  aria-label="Remove additional selections">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
            <!-- Last selected chip -->
            @if (lastSelectedOption) {
              <div class="sg-dropdown__chip">
                <span class="sg-dropdown__chip-label sg-label-small-regular">{{ lastSelectedOption.label }}</span>
                <button
                  type="button"
                  class="sg-dropdown__chip-remove"
                  tabindex="-1"
                  (mousedown)="$event.preventDefault()"
                  (click)="removeChip(lastSelectedOption, $event)"
                  [attr.aria-label]="'Remove ' + lastSelectedOption.label">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }
      </span>
    } @else {
      @if (shouldFloat) {
        <span class="sg-dropdown__placeholder" [class.sg-body-medium-regular]="size !== 'small'" [class.sg-body-small-regular]="size === 'small'">
          {{ placeholder }}
        </span>
      }
    }
    @if (showClearButton) {
      <button type="button" class="sg-dropdown__clear" tabindex="-1" (mousedown)="$event.preventDefault()" (click)="clear($event)" aria-label="Clear selection">
        <mat-icon>close</mat-icon>
      </button>
    }
    <!-- When nothing is selected the label sits at rest, so pin the info icon next to the arrow. -->
    @if (labelTooltip && !shouldFloat) {
      <sg-info-tooltip
        class="sg-dropdown__info sg-dropdown__info--field"
        variant="sg-label-small-regular"
        [heading]="labelTooltipHeading"
        [text]="labelTooltip"
        (click)="$event.stopPropagation()"></sg-info-tooltip>
    }
    <mat-icon class="sg-dropdown__arrow" [class.sg-dropdown__arrow--open]="isOpen">expand_more</mat-icon>
  </div>
</div>
<!-- Subscript area -->
@if (subscriptSizing === "fixed" || (subscriptSizing === "dynamic" && (hint || error))) {
  <div class="sg-dropdown__subscript">
    @if (hint && !error) {
      <span class="sg-dropdown__hint sg-body-xsmall-regular" @subscriptAnimation>{{ hint }}</span>
    }
    @if (error) {
      <span class="sg-dropdown__error sg-body-xsmall-regular" @subscriptAnimation>{{ error }}</span>
    }
    @if (subscriptSizing === "fixed" && !hint && !error) {
      <span class="sg-dropdown__spacer sg-body-xsmall-regular">&nbsp;</span>
    }
  </div>
}
<!-- CDK Connected Overlay for dropdown panel -->
<ng-template
  cdk-connected-overlay
  [cdkConnectedOverlayOrigin]="overlayOrigin"
  [cdkConnectedOverlayOpen]="isOpen"
  [cdkConnectedOverlayPositions]="positions"
  [cdkConnectedOverlayWidth]="panelWidth === 'trigger' ? triggerWidth : null"
  [cdkConnectedOverlayMinWidth]="panelWidth === 'auto' ? triggerWidth : null"
  [cdkConnectedOverlayScrollStrategy]="scrollStrategy"
  [cdkConnectedOverlayPanelClass]="panelClass"
  (attach)="onOverlayAttached()"
  (detach)="onOverlayDetached()">
  <div
    class="sg-dropdown__panel"
    [class.sg-dropdown__panel--small]="size === 'small'"
    role="listbox"
    [attr.aria-multiselectable]="multiple"
    (click)="onPanelClick($event)"
    @panelAnimation>
    <!-- Select All option for multi-select -->
    @if (multiple && flattenedOptions.length > 0) {
      <div
        class="sg-dropdown__option sg-dropdown__option--select-all"
        [class.sg-dropdown__option--selected]="selectAllState === 'all'"
        [class.sg-dropdown__option--indeterminate]="selectAllState === 'indeterminate'"
        role="option"
        (click)="toggleSelectAll()">
        <mat-icon class="sg-dropdown__option-checkbox">{{ selectAllIcon }}</mat-icon>
        <span class="sg-dropdown__option-label sg-body-medium-bold">Select all</span>
      </div>
    }
    @for (group of normalizedItems; track groupIndex; let groupIndex = $index; let isFirst = $first) {
      <!-- Divider between groups (only show for grouped items with multiple groups, not before first group) -->
      @if (isGrouped && !isFirst && normalizedItems.length > 1) {
        <div class="sg-dropdown__group-divider"></div>
      }
      <!-- Group header (only show if grouped and has label) -->
      @if (isGrouped && group.label) {
        <div
          class="sg-dropdown__group-header"
          [class.sg-dropdown__group-header--highlighted]="isGroupHighlighted(group)"
          role="option"
          tabindex="-1"
          (click)="toggleGroup(group)"
          (mouseenter)="highlightedIndex = getGroupNavigableIndex(group)">
          <!-- Expand/collapse chevron -->
          <mat-icon class="sg-dropdown__group-chevron" [class.sg-dropdown__group-chevron--collapsed]="group.expanded === false">expand_more</mat-icon>
          <!-- Group label -->
          <span class="sg-dropdown__group-label sg-body-small-bold">{{ group.label }}</span>
        </div>
      }
      <!-- Options (collapse when group is not expanded) -->
      @if (group.expanded !== false) {
        <div class="sg-dropdown__group-options" [@groupCollapseAnimation] [@.disabled]="groupAnimationDisabled">
          @for (option of group.options; track optionIndex; let optionIndex = $index) {
            <div
              class="sg-dropdown__option"
              [class.sg-dropdown__option--selected]="isSelected(option)"
              [class.sg-dropdown__option--highlighted]="highlightedIndex === getNavigableIndex(groupIndex, optionIndex)"
              role="option"
              [attr.aria-selected]="isSelected(option)"
              [attr.data-test]="option.optionTestId"
              (click)="selectOption(option)"
              (mouseenter)="highlightedIndex = getNavigableIndex(groupIndex, optionIndex)">
              <!-- Checkbox for multi-select -->
              @if (multiple) {
                <mat-icon class="sg-dropdown__option-checkbox">{{ isSelected(option) ? "check_box" : "check_box_outline_blank" }}</mat-icon>
              }
              <!-- Prefix icon -->
              @if (option.prefixIcon) {
                <mat-icon class="sg-dropdown__option-prefix">{{ option.prefixIcon }}</mat-icon>
              }
              <!-- Option label -->
              <span class="sg-dropdown__option-label sg-body-medium-regular">{{ option.label }}</span>
              <!-- Selected checkmark for single select -->
              @if (!multiple && isSelected(option)) {
                <mat-icon class="sg-dropdown__option-check">check</mat-icon>
              }
            </div>
          }
        </div>
      }
    }
    <!-- Empty state -->
    @if (flattenedOptions.length === 0) {
      <div class="sg-dropdown__empty sg-body-medium-regular">No options available</div>
    }
  </div>
</ng-template>
